// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable one-var, no-use-before-define, no-param-reassign */
/* eslint-disable max-len, no-var, vars-on-top, global-require */
import promiseTimeout from 'p-timeout';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import intersection from 'lodash/intersection';
import debounceDefault from 'lodash/debounce';
import omit from 'lodash/omit';
import { v4 as uuid } from 'uuid';
import eventing from '../../helpers/eventing';
import createLogger from '../../helpers/log';
import envDefault from '../../helpers/env';
import DefaultExceptionCodes from '../exception_codes';
import OTErrorClassDefault from '../ot_error_class';
import OTHelpersError from '../../common-js-helpers/error';
import createJobsQueue from '../../helpers/createJobsQueue';
import SDPHelpers from './sdp_helpers';
import shouldUsePlanBSDP from '../../helpers/shouldUsePlanBSDP';
import createExtractSenderId from '../../helpers/extractSenderId';
import hasValidPeerConnectionDefault from '../../helpers/hasValidPeerConnection';
import getApplySdpTransform from './applySdpTransform';
import getCreatePeerConnection from '../../helpers/createPeerConnection';
import getSDPTransformDefaults from './sdpTransformDefaults';
import getGetSynchronizationSources from './get_synchronization_sources';
import getIceCandidateProcessor from './ice_candidate_processor';
import getOfferProcessor from './offer_processor';
import getPeerConnectionChannels from './peer_connection_channels';
import getSubscribeProcessor from './subscribe_processor';
import getOfferAnswerProcessor from './offerAnswerProcessor';
import getActivateVideoEncodings from './activateVideoEncodings';
import defaultStatsCache from './statsCache';
import qosDefault from './qos/Qos';
import getNeedsToSwapH264Profiles from '../../helpers/needsToSwapH264Profiles';
import getErrors from '../Errors';
import getHasPictureInPictureBug from '../../helpers/hasPictureInPictureBug';
import now from '../../helpers/now';
import MediaDirection from './mediaDirection';
import createRtcStatsReportParserDefault from './createRtcStatsReportParser';

export default function PeerConnectionFactory(deps = {}) {
  const hasValidPeerConnection =
    deps.hasValidPeerConnection || hasValidPeerConnectionDefault;
  const applySdpTransform = deps.applySdpTransform || getApplySdpTransform;
  const createPeerConnection = deps.createPeerConnection || getCreatePeerConnection;
  const env = deps.env || envDefault;
  const sdpTransformDefaults = deps.sdpTransformDefaults || getSDPTransformDefaults;
  const getSynchronizationSources = deps.getSynchronizationSources || getGetSynchronizationSources;
  const IceCandidateProcessor = deps.IceCandidateProcessor || getIceCandidateProcessor;
  const createLog = deps.logging || createLogger;
  const offerProcessor = deps.offerProcessor || getOfferProcessor;
  const PeerConnectionChannels = deps.PeerConnectionChannels || getPeerConnectionChannels;
  const subscribeProcessor = deps.subscribeProcessor || getSubscribeProcessor;
  const OfferAnswerProcessor = getOfferAnswerProcessor;
  const { activateVideoEncodings } = deps.activateVideoEncodings || getActivateVideoEncodings;
  const StatsCache = deps.StatsCache || defaultStatsCache;
  const Qos = deps.Qos || qosDefault;
  const windowMock = deps.global || global;
  const debounce = deps.debounce || debounceDefault;
  const needsToSwapH264Profiles = deps.needsToSwapH264Profiles || getNeedsToSwapH264Profiles.once;
  const extractSenderId = createExtractSenderId();

  const futureIsPeerConnectionValid = () => hasValidPeerConnection(windowMock.RTCPeerConnection);

  const NativeRTCIceCandidate = deps.NativeRTCIceCandidate || windowMock.RTCIceCandidate;
  const NativeRTCSessionDescription = deps.NativeRTCSessionDescription || windowMock.RTCSessionDescription;
  const Errors = deps.Errors || getErrors;
  const OTErrorClass = deps.OTErrorClass || OTErrorClassDefault;
  const ExceptionCodes = deps.ExceptionCodes || DefaultExceptionCodes;
  const hasPictureInPictureBug = deps.hasPictureInPictureBug || getHasPictureInPictureBug;
  const createRtcStatsReportParser = deps.createRtcStatsReportParser || createRtcStatsReportParserDefault;
  // Unified-plan does not transition to failed. Plan-b used to transition after 10 secs (empirically measured)
  const DISCONNECT_TO_FAILED_TIMEOUT = 10000;

  // Helper function to forward Ice Candidates via +sendMessage+
  const iceCandidateForwarder = ({ sendMessage, logging, logAnalyticsEvent }) => {
    const sdpMids = {};
    return (event) => {
      if (event.candidate) {
        // It would be better to read the mids from the SDP
        sdpMids[event.candidate.sdpMid] = event.candidate.sdpMLineIndex;
        const { candidate } = event.candidate;
        logAnalyticsEvent('sendIceCandidate', 'Event', { candidate });

        sendMessage('candidate', {
          candidate,
          sdpMid: event.candidate.sdpMid || '',
          sdpMLineIndex: event.candidate.sdpMLineIndex || 0,
        });
      } else {
        logging.debug('IceCandidateForwarder: No more ICE candidates.');
      }
    };
  };

  const noop = () => {};

  /*
  * Negotiates a WebRTC PeerConnection.
  *
  * Responsible for:
  * * offer-answer exchange
  * * iceCandidates
  * * notification of remote streams being added/removed
  *
  */
  return function PeerConnection(options = {}) {
    let hasRelayCandidates = false;
    const {
      iceConfig = { servers: [] },
      isPublisher,
      offerOverrides,
      answerOverrides,
      p2p,
      codecFlags,
      sourceStreamId,
      sessionSimulcastEnabled,
      keyStore,
      sFrameClientStore,
      isE2ee,
      sessionId,
      preferredVideoCodecs,
    } = options;
    // Following block can be modified by subsequent actors when SPC. Please see: addOptions
    // eslint-disable-next-line prefer-const
    let {
      logAnalyticsEvent = noop,
      sendMessage: originalSendMessage,
    } = options;
    let processingOffer = false; // Whether we are currently processing an offer
    let pendingOfferMessage; // An offer we received that is pending waiting on a previous offer
    const replaceBaselineProfile = needsToSwapH264Profiles();
    let offerMessagesReceived = 0; // number of offer or generateoffer messages received
    let renegotiationInProgress = false;
    let asyncSFrameReceiverClients = {};
    let sFrameSenderClient;
    let keyStoreChangeHandler;
    const parseRtcStatsReport = createRtcStatsReportParser();
    const offerAnswerProcessor = new OfferAnswerProcessor(logAnalyticsEvent);
    const activateVideo = activateVideoEncodings(logAnalyticsEvent);

    const cleanupJobs = createJobsQueue();

    function sendMessage(type, payload) {
      if (!hasRelayCandidates) {
        const shouldCheckForRelayCandidates = [
          'candidate',
          'offer',
          'answer',
          'pranswer',
        ].indexOf(type) > -1;

        if (shouldCheckForRelayCandidates) {
          const message = type === 'candidate' ? payload.candidate : payload.sdp;
          hasRelayCandidates = message.indexOf('typ relay') !== -1;
        }
      }

      originalSendMessage(type, payload);
    }

    const startConnectingTime = now();

    logAnalyticsEvent('createPeerConnection', 'Attempt');

    const api = {};
    api.id = uuid();
    const logging = createLog(`PeerConnection:${api.id}`);

    logging.debug('construct', { id: api.id, options });

    const sdpTransforms = sdpTransformDefaults;

    const config = omit(options, [
      'isPublisher',
      'logAnalyticsEvent',
      'offerOverrides',
      'answerOverrides',
      'sendMessage',
    ]);

    let _peerConnection,
      _channels,
      _offer,
      _answer,
      _transitionToFailedTimeOut;

    let _peerConnectionCompletionHandlers = [];

    const _applyPreferredVideoCodecs = () => {
      if (env.isLegacyElectron || env.isLegacyFirefox || (env.isAndroidWebView && env.version < 139)) {
        // corrupt SDP when putting h264 in RTCRtpTransceiver.setCodecPreferences in Electron
        // no RTCRtpSender capabilities in Firefox
        // error in Android WebView below 139: InvalidModificationError: Failed to execute 'setCodecPreferences' on 'RTCRtpTransceiver': Invalid codec preferences: invalid codec with name "H264".
        // default to VP8
        return;
      }
      if (!_peerConnection?.getTransceivers) return;
      const codecsOrder = (preferredVideoCodecs?.length ? preferredVideoCodecs : []).concat('vp8');
      // Use *sender* capabilities for offerer-side codec prefs
      const caps = window.RTCRtpSender.getCapabilities?.('video');
      if (!caps || !Array.isArray(caps.codecs)) return;

      const norm = s => String(s).split('/').pop().toUpperCase();
      const want = codecsOrder.map(norm);
      const nameOf = c => norm(c.mimeType || c.name || '');
      const first = new Array(want.length); // buckets per requested codec
      const last = [];
      caps.codecs.forEach((c) => {
        const i = want.indexOf(nameOf(c));
        if (i > -1) {
          // preserve ALL variants (e.g., multiple H264/VP9 profiles)
          (first[i] ||= []).push(c);
        } else {
          // RTX/RED/ULPFEC/others stay here
          last.push(c);
        }
      });
      const prefs = first.filter(Boolean).flat().concat(last);

      // apply only to transceivers that SEND video
      const videoTransceivers = _peerConnection.getTransceivers().filter(transceiver =>
        transceiver?.sender?.track?.kind === 'video' && typeof transceiver.setCodecPreferences === 'function');
      videoTransceivers.forEach((transceiver) => {
        try {
          transceiver.setCodecPreferences(prefs);
        } catch (e) {
          // try again with just the media PTs if it throws with the full list of PTs
          const mediaOnly = prefs.filter(c =>
            /^video\//i.test(c.mimeType || '') &&
            !/\/(rtx|red|ulpfec)$/i.test(c.mimeType || '')
          );
          if (mediaOnly.length) {
            transceiver.setCodecPreferences(mediaOnly);
          }
        }
      });
    };

    const _simulcastEnabled = (() => {
      const isCapable = config.capableSimulcastStreams > 1;
      let value = sessionSimulcastEnabled && isCapable;

      return {
        get() { return value; },
        set(newValueParam) {
          // RID simulcast can be only set at initial time
          if (_peerConnection && config.enableRidSimulcast) return;

          const newValue = Boolean(newValueParam) && isCapable;

          if (config.overrideSimulcastEnabled ?? newValue !== value) {
            value = newValue;
            api.generateOfferAndSend();
          }
        },
      };
    })();

    let iceRestartTimeout;
    const clearIceRestartTimeout = () => {
      if (iceRestartTimeout) {
        clearTimeout(iceRestartTimeout);
        iceRestartTimeout = null;
      }
    };

    const _createOfferWithIceRestart = (reason, forcedRestart, recentlyIceRestartNeeded) => {
      clearIceRestartTimeout();
      if (forcedRestart || !api.iceConnectionStateIsConnected()) {
        if (forcedRestart || recentlyIceRestartNeeded) {
          api.trigger('iceRestartAttempt', { reason });
          api.generateOfferAndSend();
        } else {
          debouncedGenerateOfferAndSend(reason);
        }
      } else {
        logging.debug('iceRestart is going to wait until we disconnect or negotiationNeeded' +
         ' and then restart ice');
        iceRestartTimeout = setTimeout(() => {
          _iceRestartNeeded.set(false);
        }, 10000);
      }
    };

    const _iceRestartNeeded = (() => {
      let value = false;
      let reason;
      let lastIceRestartNeededTime = 0;

      return {
        get() { return value; },
        set(newValueParam, iceRestartReason, forcedRestart = false) {
          const newValue = Boolean(newValueParam);

          if (newValue !== value) {
            value = newValue;
            reason = iceRestartReason;

            if (value) {
              const prevIceRestartNeededTime = lastIceRestartNeededTime;
              lastIceRestartNeededTime = Date.now();
              const recentlyIceRestartNeeded = lastIceRestartNeededTime - prevIceRestartNeededTime < 10000;
              _createOfferWithIceRestart(reason, forcedRestart, recentlyIceRestartNeeded);
            } else {
              clearIceRestartTimeout();
              debouncedGenerateOfferAndSend.cancel();
            }
          }
        },
        run() {
          if (value) {
            clearIceRestartTimeout();
            debouncedGenerateOfferAndSend(reason);
          }
          return value;
        },
      };
    })();

    let candidateReadyTimer;
    let candidatesReadyResolved = false;
    const _candidatesReady = {
      clean() {
        if (candidateReadyTimer) {
          clearTimeout(candidateReadyTimer);
          candidateReadyTimer = null;
        }
        candidatesReadyResolved = true;
        delete this.promise;
        delete this.resolve;
        delete this.reject;
      },
    };

    _candidatesReady.promise = new Promise((resolve, reject) => {
      _candidatesReady.resolve = resolve;
      _candidatesReady.reject = reject;
    });

    let _iceProcessor = new IceCandidateProcessor();

    let _state = 'new';

    Object.defineProperty(
      api,
      'signalingState',
      {
        get() {
          return _peerConnection.signalingState;
        },
        set(val) {
          // obviously they should not be doing this, but we'll mimic what the browser does.
          _peerConnection.signalingState = val;
          return val;
        },
      }
    );

    eventing(api);

    api.addOptions = (opt) => {
      ({ logAnalyticsEvent } = opt);
    };

    api.startEncryption = async (connectionId) => {
      if (!isE2ee) {
        return;
      }

      const sFrameSenderId = await extractSenderId({
        sessionId,
        connectionId,
      });

      // Create an sframe sender client, which is used for encrypting the video and audio of
      // a publisher before sending it to the other side.
      sFrameSenderClient = await sFrameClientStore.createSender(sFrameSenderId);

      // Generate an encryption key based on the session ID. This key will be also needed
      // for decrypting on the subscriber side.
      const sharedKey = await keyStore.get();
      await sFrameSenderClient.setSenderEncryptionKey(sharedKey);

      keyStoreChangeHandler = (key) => { sFrameSenderClient.setSenderEncryptionKey(key); };
      keyStore.on('keyChanged', keyStoreChangeHandler);

      // SFrame uses the RTCRtpSender objects (transceiver.sender) to extract the video
      // and audio streams from the publisher and then encrypt them.
      _peerConnection.getTransceivers().forEach(async (transceiver) => {
        await sFrameSenderClient.encrypt(sFrameSenderId, transceiver.sender);
      });
    };

    api.once('iceConnected', async () => {
      const proxyInfo = '';
      const payload = {
        pcc: parseInt(now() - startConnectingTime, 10),
        hasRelayCandidates,
        proxyInfo,
      };
      if (_peerConnection && _peerConnection.proxyInfo) payload.proxyInfo = _peerConnection.proxyInfo;

      logAnalyticsEvent('createPeerConnection', 'Success', payload);
    });

    // Sometimes the receiver does not have the codecs information immediately, what makes the decryption
    // to fail. This function waits until the receiver has the codecs information or a timeout of 1 second
    // is reached (so the loop is not there forever if the codecs are not available).
    const waitUntilReceiverHasCodecs = receiver =>
      new Promise((resolve) => {
        let count = 0;
        const check = () => {
          if (receiver.getParameters().codecs.length > 0 || count >= 20) {
            resolve();
          } else {
            count += 1;
            setTimeout(check, 50);
          }
        };
        check();
      });

    api.startDecryption = async (connectionId, transceiver) => {
      if (!api.iceConnectionStateIsConnected()) {
        api.once('iceConnected', () => api.startDecryption(connectionId, transceiver));
        return;
      }
      const senderId = await extractSenderId({
        sessionId,
        connectionId,
      });
      const { receiver } = transceiver;
      const sFrameReceiverClient = await getOrCreateSFrameReceiverClient(senderId);
      try {
        await waitUntilReceiverHasCodecs(receiver);
        await sFrameReceiverClient?.decrypt(senderId, receiver);
      } catch (err) {
        logging.error(err);
      }
    };

    const getOrCreateSFrameReceiverClient = async (sFrameSenderId) => {
      if (asyncSFrameReceiverClients[sFrameSenderId]) {
        return asyncSFrameReceiverClients[sFrameSenderId];
      }
      asyncSFrameReceiverClients[sFrameSenderId] = new Promise(async (resolve) => {
        const sharedKey = await keyStore.get();

        let sFrameReceiverClient = sFrameClientStore.getReceiver(sFrameSenderId);
        if (!sFrameReceiverClient) {
          sFrameReceiverClient = await sFrameClientStore.createReceiver(sFrameSenderId);
          sFrameReceiverClient.addReceiver(sFrameSenderId);
          sFrameReceiverClient.setReceiverEncryptionKey(sFrameSenderId, sharedKey);

          keyStore.on('keyChanged', (key) => {
            sFrameReceiverClient.setReceiverEncryptionKey(sFrameSenderId, key);
          });
          sFrameReceiverClient.addEventListener('decryptFailed', () => {
            api.trigger('decryptFailed');
          });
          sFrameReceiverClient.addEventListener('decryptionRestored', () => {
            api.trigger('decryptRestored');
          });
        }
        resolve(sFrameReceiverClient);
      });
      return asyncSFrameReceiverClients[sFrameSenderId];
    };

    // Create and initialise the PeerConnection object. This deals with
    // any differences between the various browser implementations and
    // our own OTPlugin version.
    //
    // +completion+ is the function is call once we've either successfully
    // created the PeerConnection or on failure.
    //
    const internalCreatePeerConnection = (completion) => {
      logging.debug('internalCreatePeerConnection: called');

      if (_peerConnection) {
        logging.debug('internalCreatePeerConnection: resolving synchronously');
        completion.call(null, null, _peerConnection);
        return;
      }

      _peerConnectionCompletionHandlers.push(completion);

      if (_peerConnectionCompletionHandlers.length > 1) {
        // The PeerConnection is already being setup, just wait for
        // it to be ready.
        return;
      }

      logging.debug(`Creating peer connection config "${JSON.stringify(config)}".`);

      if (iceConfig.servers.length === 0) {
        // This should never happen unless something is misconfigured
        logging.error('No ice servers present');
        logAnalyticsEvent('Error', 'noIceServers');
      }

      if (iceConfig.transportPolicy === 'relay') {
        const isTurnUrl = url => (url && url.indexOf('turn') === 0);

        iceConfig.servers = iceConfig.servers
          .map((providedServer) => {
            const server = cloneDeep(providedServer);
            if (!Array.isArray(server.urls)) {
              server.urls = [server.urls];
            }
            server.urls = server.urls.filter(isTurnUrl);
            return server.urls.length === 0 ? undefined : server;
          })
          .filter(server => server !== undefined);
      }

      config.iceTransportPolicy = iceConfig.transportPolicy;

      config.iceServers = iceConfig.servers;
      if (isE2ee) {
        // Let's activate the required properties to enable Insertable Streams.
        config.forceEncodedVideoInsertableStreams = true;
        config.forceEncodedAudioInsertableStreams = true;
        config.encodedInsertableStreams = true;
      }

      futureIsPeerConnectionValid()
        .then((isValid) => {
          if (!isValid) {
            logging.error('createPeerConnection: Invalid RTCPeerConnection object');
            throw new Error('Failed to create valid RTCPeerConnection object');
          }

          return createPeerConnection({
            window: windowMock,
            config,
          });
        })
        .then(
          async (pc) => {
            attachEventsToPeerConnection(null, pc);
          },
          err => attachEventsToPeerConnection(err)
        );
    };

    // An auxiliary function to internalCreatePeerConnection. This binds the various event
    // callbacks once the peer connection is created.
    //
    // +err+ will be non-null if an err occured while creating the PeerConnection
    // +pc+ will be the PeerConnection object itself.
    //
    // @todo if anything called in attachEventsToPeerConnection throws it will be
    // silent
    const attachEventsToPeerConnection = (err, pc) => {
      if (err) {
        triggerError({
          reason: `Failed to create PeerConnection, exception: ${err}`,
          prefix: 'NewPeerConnection',
        });

        _peerConnectionCompletionHandlers = [];
        return;
      }

      logging.debug('OT attachEventsToPeerConnection');
      _peerConnection = pc;
      statsCache.setPeerConnection(pc);
      if (_iceProcessor) {
        _iceProcessor.setPeerConnection(pc);
      }
      _channels = new PeerConnectionChannels(_peerConnection);
      if (config.channels) { _channels.addMany(config.channels); }

      const forwarder = iceCandidateForwarder({ sendMessage, logging, logAnalyticsEvent });

      // Wait for a relay candidate before completing the offer so that relay
      // candidates are available as fallback when iceTransportPolicy is 'all'.
      const CANDIDATE_READY_TIMEOUT = 2000;

      const resolveCandidatesReady = (timeoutReached = false) => {
        if (candidateReadyTimer) {
          clearTimeout(candidateReadyTimer);
          candidateReadyTimer = null;
        }
        if (candidatesReadyResolved) return;
        candidatesReadyResolved = true;
        _candidatesReady.resolve();
        if (timeoutReached) {
          const msg = `In ${CANDIDATE_READY_TIMEOUT}ms the ICE gathering didn't finish and no relay candidate was received`;
          logging.warn(msg);
          logAnalyticsEvent('iceCandidatesReady', 'Event', { msg });
        }
      };

      if (candidateReadyTimer) {
        clearTimeout(candidateReadyTimer);
      }
      candidateReadyTimer = setTimeout(resolveCandidatesReady, CANDIDATE_READY_TIMEOUT, true);

      const onIceCandidate = (event) => {
        const candidate = event.candidate;
        const isRelayCandidate = candidate?.candidate?.includes('typ relay');

        if (candidate !== null && iceConfig.transportPolicy === 'relay' && !isRelayCandidate) {
          return;
        }
        if (isRelayCandidate || candidate === null) {
          // If we have a relay candidate or the gathering is completed (candidate === null),
          // resolve immediately
          resolveCandidatesReady();
        }
        forwarder(event);
      };

      const onTrackAdded = async (event) => {
        onRemoteTrackAdded(event);
      };

      let _previousIceState = _peerConnection.iceConnectionState;
      const onIceConnectionStateChanged = (event) => {
        if (_peerConnection) {
          logging.debug('iceconnectionstatechange', _peerConnection.iceConnectionState);
          if (_peerConnection.iceConnectionState === 'connected') {
            // Now that the iceConnectionState is connected, let's make sure the transitionToFailedTimeOut
            // is cleared ASAP to avoid triggering an iceConnectionStateChange event to failed.
            api.clearFailedTimeout();
            api.emit('iceConnected');
            _iceRestartNeeded.set(false);
          } else if (_peerConnection.iceConnectionState === 'completed' && env.isLegacyEdge) {
            // Start collecting stats later in Edge because it fails if you call it sooner
            // This can probably be fixed better in Adapter.js
            setTimeout(() => qos.startCollecting(), 1000);
          }
        } else {
          logging.debug('iceconnectionstatechange on peerConnection that no longer exists', api);
        }

        const newIceState = event.target.iceConnectionState;
        api.trigger('iceConnectionStateChange', newIceState);

        logAnalyticsEvent('attachEventsToPeerConnection', 'iceconnectionstatechange',
          newIceState);

        if (newIceState === 'disconnected') {
          if (_iceRestartNeeded.run()) {
            logging.debug('Restarting ice!');
          } else if (!shouldUsePlanBSDP()) {
            // We only transition to failed in unified-plan
            // Plan-b will transition natively
            _transitionToFailedTimeOut = setTimeout(() => {
              _transitionToFailedTimeOut = null;
              const iceState = 'failed';
              logAnalyticsEvent('attachEventsToPeerConnection', 'iceconnectionstatechange',
                iceState);
              _previousIceState = iceState;
              api.trigger('iceConnectionStateChange', iceState);
            }, DISCONNECT_TO_FAILED_TIMEOUT);
          }
        }

        if (_previousIceState !== 'disconnected' && newIceState === 'failed') {
          // the sequence disconnected => failure would indicate an abrupt disconnection (e.g. remote
          // peer closed the browser) or a network problem. We don't want to log that has a connection
          // establishment failure. This behavior is seen only in Chrome 47+

          triggerError({
            reason: 'The stream was unable to connect due to a network error.' +
              ' Make sure your connection isn\'t blocked by a firewall.',
            prefix: 'ICEWorkflow',
          });
        }

        _previousIceState = newIceState;
      };

      const onNegotiationNeeded = () => {
        logAnalyticsEvent('peerConnection:negotiationNeeded', 'Event');
        if (isPublisher) {
          api.generateOfferAndSend();
        }
        api.trigger('negotiationNeeded');
      };

      const onIceGatheringStateChange = () => {
        if (_peerConnection.iceGatheringState === 'complete') {
          // Gathering is done — resolve even if no relay candidate arrived
          resolveCandidatesReady();
        }
      };

      _peerConnection.addEventListener('track', onTrackAdded);
      _peerConnection.addEventListener('icecandidate', onIceCandidate);
      _peerConnection.addEventListener('signalingstatechange', routeStateChanged);
      _peerConnection.addEventListener('negotiationneeded', onNegotiationNeeded);
      _peerConnection.addEventListener('iceconnectionstatechange', onIceConnectionStateChanged);
      _peerConnection.addEventListener('icegatheringstatechange', onIceGatheringStateChange);

      cleanupJobs.add(() => {
        if (!_peerConnection) {
          return;
        }

        _peerConnection.removeEventListener('track', onTrackAdded);
        _peerConnection.removeEventListener('icecandidate', onIceCandidate);
        _peerConnection.removeEventListener('signalingstatechange', routeStateChanged);
        _peerConnection.removeEventListener('negotiationneeded', onNegotiationNeeded);
        _peerConnection.removeEventListener('iceconnectionstatechange', onIceConnectionStateChanged);
        _peerConnection.removeEventListener('icegatheringstatechange', onIceGatheringStateChange);
      });

      triggerPeerConnectionCompletion(null);
    };

    const triggerPeerConnectionCompletion = () => {
      while (_peerConnectionCompletionHandlers.length) {
        _peerConnectionCompletionHandlers.shift().call(null);
      }
    };

    // Clean up the Peer Connection and trigger the close event.
    // This function can be called safely multiple times, it will
    // only trigger the close event once (per PeerConnection object)
    const tearDownPeerConnection = () => {
      // Our connection is dead, stop processing ICE candidates
      if (_iceProcessor) {
        _iceProcessor.destroy();
        _iceProcessor = null;
      }

      _peerConnectionCompletionHandlers = [];

      qos.stopCollecting();
      cleanupJobs.releaseAll();
      _candidatesReady.clean();
      statsCache.destroy();

      if (_peerConnection !== null) {
        if (_peerConnection.destroy) {
          // OTPlugin defines a destroy method on PCs. This allows
          // the plugin to release any resources that it's holding.
          _peerConnection.destroy();
        }

        _peerConnection = null;
        api.trigger('close');
      }

      api.off();
    };

    const routeStateChanged = (event) => {
      const newState = event.target.signalingState;

      api.emit('signalingStateChange', newState);

      if (newState === 'stable') {
        api.emit('signalingStateStable');
      }

      if (newState && newState !== _state) {
        _state = newState;
        logging.debug(`stateChange: ${_state}`);

        switch (_state) {
          case 'closed':
            tearDownPeerConnection();
            break;
          default:
        }
      }
    };

    const qosCallback = (parsedStats) => {
      parsedStats.dataChannels = _channels.sampleQos();
      api.trigger('qos', { parsedStats, simulcastEnabled: _simulcastEnabled.get() });
    };

    const getRemoteStreams = () => {
      let streams;

      if (_peerConnection.getRemoteStreams) {
        streams = _peerConnection.getRemoteStreams();
      } else if (_peerConnection.remoteStreams) {
        streams = _peerConnection.remoteStreams;
      } else {
        throw new Error('Invalid Peer Connection object implements no ' +
          'method for retrieving remote streams');
      }

      // Force streams to be an Array, rather than a 'Sequence' object,
      // which is browser dependent and does not behaviour like an Array
      // in every case.
      return Array.prototype.slice.call(streams);
    };

    // PeerConnection signaling
    const onRemoteTrackAdded = (event) => {
      const { track, transceiver } = event;
      // To be safe we consider this event can be either RTCTrackEvent or MediaStreamEvent type.
      // - RTCTrackEvent.streams => array with all the streams associated with the added track.
      //   Only one stream should be associated to the added track.
      // - MediaStreamEvent.stream => stream added
      const stream = event.stream || event.streams?.[0];
      logAnalyticsEvent('createPeerConnection', 'TrackAdded');
      api.trigger('trackAdded', { stream, track, transceiver });
    };

    // ICE Negotiation messages

    const reportError = (message, reason, prefix, offerSdp, answerSdp) => {
      logAnalyticsEvent('peerConnection:processOffer', 'Failed', { error: `${message}: ${reason}`, offerSdp, answerSdp });
      processingOffer = false;
      triggerError({ reason: `PeerConnection.offerProcessor ${message}: ${reason}`, prefix });
    };

    // Process an offer that
    const processOffer = (message) => {
      if (processingOffer) {
        // If we get multiple pending offer messages we just handle the most recent one
        pendingOfferMessage = message;
        return;
      }
      processingOffer = true;
      logAnalyticsEvent('peerConnection:processOffer', 'Attempt');
      const offer = new NativeRTCSessionDescription({ type: 'offer', sdp: message.content.sdp });
      // Relays +answer+ Answer
      const relayAnswer = (answer, modifiedOffer) => {
        processingOffer = false;
        if (_iceProcessor) {
          _iceProcessor.process();
        }

        logAnalyticsEvent('peerConnection:processOffer', 'Success', { sdp: modifiedOffer.sdp });
        logAnalyticsEvent('peerConnection:processAnswer', 'Attempt');
        logAnalyticsEvent('peerConnection:processAnswer', 'Success', { sdp: answer.sdp });

        sendMessage('answer', answer);

        if (!env.isLegacyEdge) {
          qos.startCollecting();
        }

        if (pendingOfferMessage) {
          processOffer(pendingOfferMessage);
          pendingOfferMessage = null;
        }
      };

      const onRemoteVideoSupported = supported => api.trigger('remoteVideoSupported', supported);

      internalCreatePeerConnection(() => {
        offerProcessor(
          _peerConnection,
          windowMock.RTCPeerConnection,
          windowMock.RTCSessionDescription,
          NativeRTCSessionDescription,
          sdpTransforms,
          offer,
          codecFlags,
          p2p,
          (answer, modifiedOffer) => _candidatesReady.promise.then(() => relayAnswer(answer, modifiedOffer)),
          reportError,
          onRemoteVideoSupported,
          replaceBaselineProfile,
          sourceStreamId
        );
      });
    };

    const processAnswer = (message) => {
      logAnalyticsEvent('peerConnection:processAnswer', 'Attempt');
      const errorMessageFixedInChrome71 = 'Failed to parse SessionDescription. a=extmap-allow-mixed Expects at least 2 fields.';
      const failure = (errorReason) => {
        if (typeof errorReason.includes === 'function' && errorReason.includes(errorMessageFixedInChrome71)) {
          errorReason = 'SessionDescription issue has been fixed in Chrome 71 and above. Please update your browser.';
        }
        const payload = { error: errorReason };
        if (_answer) {
          payload.sdp = _answer.sdp;
        }
        logAnalyticsEvent('peerConnection:processAnswer', 'Failed', payload);
        triggerError({
          reason: `Error while setting RemoteDescription ${errorReason}`,
          prefix: 'SetRemoteDescription',
        });
      };

      if (!message.content.sdp) {
        failure('Weird answer message, no SDP.');
        return;
      }

      _answer = new NativeRTCSessionDescription({
        type: 'answer',
        sdp: applySdpTransform(
          sdpTransforms,
          'remote',
          'answer',
          assign({ replaceBaselineProfile }, answerOverrides),
          message.content.sdp
        ).local,
      });

      (() => {
        const success = () => {
          logging.debug('processAnswer: setRemoteDescription Success');
          logAnalyticsEvent('peerConnection:processAnswer', 'Success', { sdp: _answer.sdp });
          if (_iceProcessor) {
            _iceProcessor.process();
          }
        };

        _peerConnection.setRemoteDescription(_answer)
          .then(success)
          .catch(failure);

        // Once answer is received, the offerAnswer process is resolved.
        offerAnswerProcessor.setResolved();
      })();

      if (!env.isLegacyEdge) {
        qos.startCollecting();
      }
    };

    const processSubscribe = (onOfferCreated) => {
      if (_peerConnection?.signalingState?.toLowerCase() === 'closed') {
        return;
      }
      logAnalyticsEvent('peerConnection:processSubscribe', 'Attempt');
      logging.debug('processSubscribe: Sending offer to subscriber.');

      let numSimulcastStreams = _simulcastEnabled.get() && !config.enableRidSimulcast
        ? config.capableSimulcastStreams : 1;

      // When transitioning from Mantis to P2P, in order to ensure the video quality we must
      // send only one video layer, regardless that simulcast is enabled.
      const isAdaptiveP2pSourceStreamId = sourceStreamId === 'P2P' && !p2p;
      if (isAdaptiveP2pSourceStreamId) {
        numSimulcastStreams = 1;
      }

      internalCreatePeerConnection(() => {
        subscribeProcessor({
          peerConnection: _peerConnection,
          NativeRTCSessionDescription,
          sdpTransforms,
          numSimulcastStreams,
          removeVideoOrientation: hasPictureInPictureBug(),
          offerOverrides,
          offerConstraints: { iceRestart: _iceRestartNeeded.get() },
          replaceBaselineProfile,
        }).then(
          (offer) => {
            logging.debug('processSubscribe: got offer, waiting for ' +
              '_readyToCompleteOffer');

            _offer = offer;
            _candidatesReady.promise.then(() => onOfferCreated(_offer));
            logAnalyticsEvent('peerConnection:processSubscribe', 'Success', { sdp: _offer.sdp });
          },
          (error) => {
            logAnalyticsEvent('peerConnection:processSubscribe', 'Failed', { error: error.message });
            triggerError({
              reason: `subscribeProcessor ${error.message}: ${error.reason}`,
              prefix: error.prefix,
            });
          }
        );

        _iceRestartNeeded.set(false);
      });
    };

    const cleanupSFrameClients = () => {
      if (typeof keyStoreChangeHandler === 'function') {
        keyStore.off('keyChanged', keyStoreChangeHandler);
      }
      sFrameSenderClient = null;
      asyncSFrameReceiverClients = null;
    };

    api.setSendMessage = (newSendMessage) => {
      originalSendMessage = newSendMessage;
    };

    api.generateOfferAndSend = () => {
      const offerSender = (offer) => {
        logging.debug('processSubscribe: sending offer');
        sendMessage('offer', offer);
      };
      api.generateOffer(offerSender);
    };

    const debouncedGenerateOfferAndSend = debounce((reason) => {
      api.trigger('iceRestartAttempt', { reason });
      api.generateOfferAndSend();
    }, 2000);

    api.generateOffer = debounce((onOfferCreated = () => {}) => {
      // processSubscribe will handle offer creation, thus it is enqueued.
      offerAnswerProcessor.enqueueOfferAnswer(() =>
        processSubscribe(onOfferCreated));
    }, 100);

    const triggerError = ({ reason, prefix }) => {
      logging.error(
        reason,
        'in state',
        !_peerConnection ? '(none)' : {
          connectionState: _peerConnection.connectionState,
          iceConnectionState: _peerConnection.iceConnectionState,
          iceGatheringState: _peerConnection.iceGatheringState,
          signalingState: _peerConnection.signalingState,
        }
      );

      api.trigger('error', { reason, prefix });
    };

    /*
    * Add a track to the underlying PeerConnection
    *
    * @param {object} track - the track to add
    * @param {object} stream - the stream to add it to
    * @return {RTCRtpSender}
    */
    api.addTrack = (track, stream) => {
      const promise = new Promise((resolve, reject) => {
        internalCreatePeerConnection((err) => {
          if (err) { return reject(err); }
          resolve();
          return undefined;
        });
      })
        .then(() => {
          if (_peerConnection.addTrack) {
            return _peerConnection.addTrack(track, stream);
          }

          const pcStream = _peerConnection.getLocalStreams()[0];
          if (pcStream === undefined) {
            throw new Error('PeerConnection has no existing streams, cannot addTrack');
          }
          pcStream.addTrack(track);
          api.generateOfferAndSend();
          return undefined;
        })
        .then(() => new Promise((resolve) => {
          api.once('signalingStateStable', () => {
            resolve();
          });
        }));
      return promiseTimeout(promise, 15000, 'Renegotiation timed out');
    };

    function FakeRTCRtpSender(track) {
      this.track = track;
    }

    api.setIceConfig = (newIceConfig) => {
      try {
        // Firefox does not follow the spec laid out in MDN for attempts to change the certs
        // after they've already been set. The documentation states the following about certificates:
        // once the certificates have been set, this property is ignored in future calls to
        // RTCPeerConnection.setConfiguration().
        if (env.isFirefox) {
          newIceConfig.certificates = _peerConnection.getConfiguration().certificates;
        }
        _peerConnection.setConfiguration(newIceConfig);
      } catch (err) {
        throw (new OTHelpersError(`A peer connection failed to set configuration with ${err.message}`,
          Errors.FAILED_SET_CONFIGURATION));
      }

      _iceRestartNeeded.set(true);
      return _peerConnection;
    };

    /**
     * Remove a track from the underlying PeerConnection
     *
     * @param {RTCRtpSender} RTCRtpSender - the RTCRtpSender to remove
     */
    api.removeTrack = (RTCRtpSender) => {
      const promise = Promise.resolve()
        .then(() => {
          if (RTCRtpSender instanceof FakeRTCRtpSender) {
            _peerConnection.getLocalStreams()[0].removeTrack(RTCRtpSender.track);
            api.generateOfferAndSend();
            return undefined;
          }
          return _peerConnection.removeTrack(RTCRtpSender);
        })
        .then(() => new Promise((resolve) => {
          api.once('signalingStateStable', () => {
            resolve();
          });
        }));
      return promiseTimeout(promise, 15000, 'Renegotiation timed out');
    };

    const shouldUseRIDSimulcast = () =>
      _simulcastEnabled.get() && config.enableRidSimulcast && config.capableSimulcastStreams > 1 && config.sourceStreamId !== 'P2P';

    api.addLocalStream = webRTCStream => new Promise((resolve, reject) => {
      const addStream = () => {
        // node does not support getTracks
        if (!webRTCStream.getTracks) return;
        const useRIDSimulcast = shouldUseRIDSimulcast();
        webRTCStream.getTracks().forEach((track) => {
          if (track.kind === 'video' && useRIDSimulcast) {
            // scalabilityMode compatible with VP8 and VP9
            const scalabilityMode = 'L1T3';
            const sendEncodings = [
              { rid: 'f', scaleResolutionDownBy: 1.0, scalabilityMode },
              { rid: 'h', scaleResolutionDownBy: 2.0, scalabilityMode },
            ];
            if (config.capableSimulcastStreams > 2) {
              sendEncodings.push({ rid: 'q', scaleResolutionDownBy: 4.0, scalabilityMode });
            }
            _peerConnection.addTransceiver(track, { sendEncodings });
          } else {
            _peerConnection.addTrack(track, webRTCStream);
          }
        });
      };

      internalCreatePeerConnection((err) => {
        if (err) {
          reject(err);
          return;
        }
        try {
          addStream();
        } catch (addStreamErr) {
          reject(addStreamErr);
          return;
        }
        try {
          _applyPreferredVideoCodecs();
        } catch (videoCodecOrderError) {
          logging.error(videoCodecOrderError);
        }
        resolve();
      }, webRTCStream);
    });

    api.getLocalStreams = () => _peerConnection.getLocalStreams();

    api.getSenders = () => {
      if (_peerConnection.getSenders) {
        return _peerConnection.getSenders();
      }

      return _peerConnection.getLocalStreams()[0].getTracks().map(track => new FakeRTCRtpSender(track));
    };

    api.disconnect = () => {
      if (_iceProcessor) {
        _iceProcessor.destroy();
        _iceProcessor = null;
      }

      if (_peerConnection?.signalingState &&
        _peerConnection.signalingState.toLowerCase() !== 'closed') {
        _peerConnection.close();
        cleanupSFrameClients();
        setTimeout(tearDownPeerConnection);
      }
    };

    api.iceRestart = (reason, forcedRestart) => _iceRestartNeeded.set(true, reason, forcedRestart);

    api.clearFailedTimeout = () => {
      if (_transitionToFailedTimeOut) {
        clearTimeout(_transitionToFailedTimeOut);
        _transitionToFailedTimeOut = null;
      }
    };

    api.activateVideoEncodings = () => activateVideo(_peerConnection, true);

    api.deactivateVideoEncodings = () => activateVideo(_peerConnection, false);

    api.processMessage = (type, message) => {
      logging.debug(`processMessage: Received ${
        type} from ${message.fromAddress}`, message);

      logging.debug(message);

      switch (type) {
        case 'generateoffer':
          if (
            message.content &&
            message.content.simulcastEnabled !== undefined
          ) {
            _simulcastEnabled.set(message.content.simulcastEnabled);
          }

          api.generateOfferAndSend();
          trackRenegotiationAttempts();
          break;

        case 'offer':
          processOffer(message);
          trackRenegotiationAttempts();
          break;

        case 'answer':
        case 'pranswer':
          processAnswer(message);
          break;

        case 'candidate':
          if (_iceProcessor) {
            const iceCandidate = new NativeRTCIceCandidate(message.content);
            let error;
            const payload = { iceCandidate };
            logAnalyticsEvent('addIceCandidate', 'Attempt', payload);

            _iceProcessor.addIceCandidate(iceCandidate).catch((err) => {
              // Sometimes you might get an error adding an iceCandidate
              // this does not mean we should fail, if we get a working candidate
              // later then we should let it work
              error = `Error while adding ICE candidate: ${JSON.stringify(iceCandidate)}: ${err.toString()}`;
              payload.error = error;
              logging.warn(error);
            });
            logAnalyticsEvent('addIceCandidate', error ? 'Failed' : 'Success', payload);
          }

          break;

        default:
          logging.debug(`processMessage: Received an unexpected message of type ${
            type} from ${message.fromAddress}: ${JSON.stringify(message)}`);
      }

      function trackRenegotiationAttempts() {
        offerMessagesReceived += 1;
        qos.handleOfferMessageReceived();

        if (offerMessagesReceived > 1) {
          logAnalyticsEvent('Renegotiation', 'Attempt');
          renegotiationInProgress = true;
        }
      }

      return api;
    };

    const _sortTransceivers = (transceivers) => {
      // Tranceivers are sorted in reverse order, so we only stop the first transceiver in the
      // PC once.
      const sorter = (t1, t2) => {
        // This will swap the positions between t1 and t2.
        if (t1.mid < t2.mid) {
          return 1;
        }
        // This will keep the same positions.
        if (t1.mid > t2.mid) {
          return -1;
        }
        // This should not happen, but just to be safe.
        return 0;
      };
      return transceivers.sort(sorter);
    };

    const _getTransceivers = transceiverIds => _peerConnection.getTransceivers()
      .filter(transceiver => transceiverIds.includes(transceiver.mid));

    api.getTransceivers = () => _peerConnection.getTransceivers();

    api.stopTransceivers = (transceiverIds) => {
      // transceivers provides an array of transceiver.mid. This will stop every transceiver
      // provided in the PC.
      const transceiversToStop = _getTransceivers(transceiverIds);

      _sortTransceivers(transceiversToStop).forEach(transceiver => transceiver.stop());
    };

    api.setTransceiversToInactive = (transceiverIds) => {
      const transceivers = _getTransceivers(transceiverIds);

      transceivers.forEach((transceiver) => { transceiver.direction = MediaDirection.INACTIVE; });
    };

    api.remoteStreams = () => (_peerConnection ? getRemoteStreams() : []);

    api.remoteTracks = () => {
      if (!_peerConnection) {
        return [];
      }

      if (_peerConnection.getReceivers) {
        return Array.prototype.slice.apply(_peerConnection.getReceivers())
          .map(receiver => receiver.track);
      }

      return Array.prototype.concat.apply([],
        getRemoteStreams()
          .map(stream => stream.getTracks())
      );
    };

    api.remoteDescription = () => _peerConnection.remoteDescription;

    /**
     * @param {function(DOMError, Array<RTCStats>)} callback
     */
    api.getStats = (track, source, callback) => {
      if (!_peerConnection) {
        const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
        callback(new OTHelpersError(OTErrorClass.getTitleByCode(errorCode),
          Errors.PEER_CONNECTION_NOT_CONNECTED, {
            code: errorCode,
          }));
        return;
      }
      statsCache.getRtcStatsReport(track, source).then(stats => callback(null, parseRtcStatsReport(stats))).catch(callback);
    };

    /**
     * @param {function(DOMError, RTCStatsReport)} callback
     */
    api.getRtcStatsReport = (track, source, callback) => {
      if (!_peerConnection) {
        const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
        callback(new OTHelpersError(OTErrorClass.getTitleByCode(errorCode),
          Errors.PEER_CONNECTION_NOT_CONNECTED, {
            code: errorCode,
          }));
        return;
      }
      statsCache.getRtcStatsReport(track, source).then(stats => callback(null, stats)).catch(callback);
    };

    api.getSynchronizationSources = (callback) => {
      if (!isPublisher) {
        if (!_peerConnection) {
          const errorCode = ExceptionCodes.PEER_CONNECTION_NOT_CONNECTED;
          callback(new OTHelpersError(OTErrorClass.getTitleByCode(errorCode),
            Errors.PEER_CONNECTION_NOT_CONNECTED, {
              code: errorCode,
            }));
          return;
        }
        getSynchronizationSources(_peerConnection, callback);
      }
    };

    const waitForChannel = function waitForChannel(timesToWait, label, channelOptions, completion) {
      let err;
      const channel = _channels.get(label, channelOptions);

      if (!channel) {
        if (timesToWait > 0) {
          setTimeout(
            waitForChannel.bind(null, timesToWait - 1, label, channelOptions, completion),
            200
          );

          return;
        }

        err = new OTHelpersError(`${'A channel with that label and options could not be found. ' +
                              'Label:'}${label}. Options: ${JSON.stringify(channelOptions)}`);
      }

      completion(err, channel);
    };

    api.getDataChannel = (label, channelOptions, completion) => {
      if (!_peerConnection) {
        completion(new OTHelpersError('Cannot create a DataChannel before there is a connection.'));
        return;
      }
      // Wait up to 20 sec for the channel to appear, then fail
      waitForChannel(100, label, channelOptions, completion);
    };

    api.getSourceStreamId = () => sourceStreamId;

    api.iceConnectionStateIsConnected = () => _peerConnection && ['connected', 'completed'].indexOf(_peerConnection.iceConnectionState) > -1;

    api.hasTrack = track => _peerConnection.getSenders().some(sender => sender.track === track);

    api.findAndReplaceTrack = (oldTrack, newTrack) => Promise.resolve()
      .then(() => {
        const sender = _peerConnection.getSenders()
          .filter(s => s.track === oldTrack)[0];

        if (!sender) {
          // There is no video track to replace but this is OK
          // they probably called cycleVideo on an audio only Publisher
          // or on a Publisher that does not support the videoCodec
          return Promise.resolve();
        }

        if (typeof sender.replaceTrack !== 'function') {
          throw new Error('Sender does not support replaceTrack');
        }

        return sender.replaceTrack(newTrack);
      });

    api.hasRelayCandidates = () => hasRelayCandidates;

    api.getNegotiatedCodecs = () => {
      if (!_peerConnection.localDescription || !_peerConnection.remoteDescription) {
        return null;
      }

      const getForMediaType = mediaType => intersection(
        SDPHelpers.getCodecs(_peerConnection.localDescription.sdp, mediaType),
        SDPHelpers.getCodecs(_peerConnection.remoteDescription.sdp, mediaType)
      );

      return {
        audio: getForMediaType('audio'),
        video: getForMediaType('video'),
      };
    };

    api.on('signalingStateStable', () => {
      if (renegotiationInProgress) {
        renegotiationInProgress = false;
        logAnalyticsEvent('Renegotiation', 'Success', api.getNegotiatedCodecs());
      }
    });

    api.on('error', ({ prefix }) => {
      if (renegotiationInProgress && ['CreateOffer', 'SetRemoteDescription'].indexOf(prefix) !== -1) {
        renegotiationInProgress = false;
        logAnalyticsEvent('Renegotiation', 'Failure');
      }
    });

    api.on('close', () => {
      if (renegotiationInProgress) {
        renegotiationInProgress = false;
        logAnalyticsEvent('Renegotiation', 'Cancel');
      }
    });

    const statsCache = new StatsCache();

    const qos = new Qos(statsCache, isPublisher);
    qos.on('stats', qosCallback);

    return api;
  };
}
